import { assert } from '../assert/assert.js'
import { DurationLike, UnixDuration } from '../time/duration/UnixDuration.js'
import { LazyTransform } from './lazyPipe.js'
import { LazyPromise } from './types.js'

export interface TimeoutParams<T> {
  fn: LazyPromise<T>
  timeout: DurationLike
  error?: Error
}

export function timeout<T>(args: TimeoutParams<T>): Promise<T> {
  const { fn, timeout, error } = args
  const timeoutDuration = UnixDuration.fromDurationLike(timeout)
  let rejectTimeoutId: ReturnType<typeof setTimeout> | undefined

  function rejectTimeoutPromise(): Promise<never> {
    return new Promise<never>((_, reject) => {
      rejectTimeoutId = setTimeout(() => {
        reject(error ?? new TimeoutError(timeoutDuration))
      }, Number(timeoutDuration.toMilliseconds()))
    })
  }

  return Promise.race([
    rejectTimeoutPromise(),
    fn().finally(() => {
      assert(rejectTimeoutId !== undefined)
      clearTimeout(rejectTimeoutId)
    }),
  ])
}

export class TimeoutError extends Error {
  constructor(timeout: UnixDuration) {
    super(`Timed out after ${timeout.format()}`)
    this.name = 'TimeoutError'
  }
}

timeout.lazy = <T>(options: Omit<TimeoutParams<T>, 'fn'>): LazyTransform<T, T> => {
  return (fn) => () => timeout({ ...options, fn })
}
